import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, X, RefreshCw, ArrowLeft, Mail, Bell, Trophy, Phone, KeyRound, Eye, EyeOff, Lock, Wifi, DollarSign, CreditCard } from 'lucide-react';
import { fmtDate, fmtDateShort } from '../utils/dates';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  active:       { label: 'Active',        color: '#00E676', bg: '#00E67614' },
  trial:        { label: 'Trial',         color: '#00BCD4', bg: '#00BCD414' },
  pending:      { label: 'Pending',       color: '#FFB300', bg: '#FFB30014' },
  grace_period: { label: 'Grace Period',  color: '#FF9800', bg: '#FF980014' },
  suspended:    { label: 'Suspended',     color: '#FF2D55', bg: '#FF2D5514' },
  deactivated:  { label: 'Deactivated',   color: '#555',    bg: '#1A1A1A'  },
};
const PLAN_CONFIG = {
  founder: { label: 'Founder',   color: '#FFD700' },
  tenant:  { label: 'Tenant',    color: '#A3A3A3' },
  trial:   { label: 'Trial',     color: '#00BCD4' },
};

function sportsAdminApi(token) {
  return axios.create({
    baseURL: BACKEND,
    headers: { Authorization: `Bearer ${token}` },
  });
}

// Days from today until a YYYY-MM-DD date (negative = already past).
function daysUntil(ymd) {
  if (!ymd) return null;
  const t = new Date(`${ymd.slice(0, 10)}T00:00:00`);
  if (isNaN(t)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((t - today) / 86400000);
}

// Color-coded one-liner describing a tenant's renewal state, shown on the org
// card so expiry is visible WITHOUT opening the detail modal.
function RenewalLine({ org }) {
  if (org.plan === 'founder') return null;
  if (org.account_status === 'trial' || org.account_status === 'pending') return null;
  if (!org.next_renewal_date) {
    if (org.plan === 'tenant' && ['active', 'grace_period'].includes(org.account_status)) {
      return <p style={{ fontSize: 10, color: '#FF9800', marginTop: 2 }}>No expiry date set — billing sweep can't reach this tenant</p>;
    }
    return null;
  }
  if (org.account_status !== 'active') return null; // grace/suspended cards have their own line
  const d = daysUntil(org.next_renewal_date);
  const color = d < 0 ? '#FF2D55' : d <= 7 ? '#FF9800' : '#00E676';
  const label = d < 0 ? `${-d} day${-d === 1 ? '' : 's'} overdue` : d === 0 ? 'expires today' : `${d} day${d === 1 ? '' : 's'} left`;
  return (
    <p style={{ fontSize: 10, color, marginTop: 2 }}>
      Renews {fmtDate(org.next_renewal_date)} · {label}
      {org.last_renewed_at ? <span style={{ color: '#555' }}> · last renewed {fmtDate(org.last_renewed_at)}</span> : null}
    </p>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || { label: status, color: '#A3A3A3', bg: '#1A1A1A' };
  return (
    <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 5, background: c.bg, color: c.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {c.label}
    </span>
  );
}

function ApproveModal({ org, api, onDone }) {
  const [plan, setPlan] = useState('tenant');
  const [rate, setRate] = useState('');
  const [maxCourts, setMaxCourts] = useState('');
  const [trialDays, setTrialDays] = useState('14');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const inp = { background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '8px 12px', width: '100%', outline: 'none', fontSize: 13, boxSizing: 'border-box' };

  const handleApprove = async () => {
    setSaving(true);
    try {
      await api.put(`/api/sports/admin/orgs/${org.id}/approve`, {
        plan, monthly_rate: parseFloat(rate || 0),
        max_courts: maxCourts ? parseInt(maxCourts) : null,
        trial_days: plan === 'trial' ? parseInt(trialDays || 14) : 0,
        admin_notes: notes,
      });
      toast.success(`${org.name} approved as ${plan}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.88)' }}>
      <div className="rounded-xl w-full max-w-md" style={{ background: '#141414', border: '1px solid #2A2A2A' }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #1E1E1E' }}>
          <p className="font-bold uppercase text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>Approve: {org.name}</p>
          <button onClick={onDone} style={{ color: '#555' }}><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wide mb-1" style={{ color: '#737373' }}>Plan</label>
            <select value={plan} onChange={e => setPlan(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="tenant">Tenant (Monthly)</option>
              <option value="trial">Trial (Limited Days)</option>
              <option value="founder">Founder (Unlimited)</option>
            </select>
          </div>
          {plan === 'trial' && (
            <div>
              <label className="block text-xs uppercase tracking-wide mb-1" style={{ color: '#737373' }}>Trial Days</label>
              <input type="number" min="1" value={trialDays} onChange={e => setTrialDays(e.target.value)} style={inp} placeholder="e.g. 14" />
            </div>
          )}
          {plan !== 'founder' && (
            <div>
              <label className="block text-xs uppercase tracking-wide mb-1" style={{ color: '#737373' }}>Monthly Rate (₱)</label>
              <input type="number" min="0" value={rate} onChange={e => setRate(e.target.value)} style={inp} placeholder="e.g. 999 (0 = negotiated later)" />
            </div>
          )}
          <div>
            <label className="block text-xs uppercase tracking-wide mb-1" style={{ color: '#737373' }}>Max Courts (blank = unlimited)</label>
            <input type="number" min="1" value={maxCourts} onChange={e => setMaxCourts(e.target.value)} style={inp} placeholder="Leave blank for unlimited" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide mb-1" style={{ color: '#737373' }}>Admin Notes</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} style={inp} placeholder="Internal notes…" />
          </div>
          <button onClick={handleApprove} disabled={saving} data-testid={`admin-approve-${org.id}`}
            className="w-full py-2.5 rounded-lg font-bold uppercase text-sm"
            style={{ background: '#00E676', color: '#000', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Approving…' : 'Activate Account'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrgCard({ org, api, onRefresh }) {
  const [showApprove, setShowApprove] = useState(false);
  const navigate = useNavigate();
  const p = PLAN_CONFIG[org.plan] || PLAN_CONFIG.tenant;

  return (
    <>
      {showApprove && <ApproveModal org={org} api={api} onDone={() => { setShowApprove(false); onRefresh(); }} />}

      <div data-testid={`sports-admin-org-${org.id}`}
        onClick={() => navigate(`/sports/super-admin/tenants/${org.id}`)}
        style={{ background: '#141414', border: `1px solid ${org.account_status === 'pending' ? '#FFB30040' : '#1E1E1E'}`, borderRadius: 12, cursor: 'pointer' }}>
        <div className="flex items-center gap-3 px-5 py-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-sm uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{org.name}</p>
              <StatusBadge status={org.account_status} />
              <span style={{ fontSize: 10, color: p.color, fontWeight: 700 }}>{p.label}</span>
            </div>
            <p className="text-xs mt-0.5" style={{ color: '#555' }}>
              /{org.slug} · {org.owner_email}
              {org.monthly_rate > 0 ? ` · ₱${org.monthly_rate?.toLocaleString()}/mo` : ''}
              {` · ${org.courts_count || 0} courts · ${org.bookings_count || 0} bookings`}
            </p>
            {org.account_status === 'grace_period' && org.next_renewal_date && (
              <p style={{ fontSize: 10, color: '#FF9800', marginTop: 2 }}>Grace period — due: {fmtDate(org.next_renewal_date)}</p>
            )}
            {org.account_status === 'trial' && org.trial_ends_at && (
              <p style={{ fontSize: 10, color: '#00BCD4', marginTop: 2 }}>Trial ends: {fmtDate(org.trial_ends_at)}</p>
            )}
            <RenewalLine org={org} />
          </div>
          <div className="flex items-center gap-2">
            {org.account_status === 'pending' && (
              <button onClick={e => { e.stopPropagation(); setShowApprove(true); }}
                data-testid={`sports-admin-approve-btn-${org.id}`}
                className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase"
                style={{ background: '#00E676', color: '#000' }}>
                Approve
              </button>
            )}
            <ChevronRight className="h-4 w-4" style={{ color: '#555' }} />
          </div>
        </div>
      </div>
    </>
  );
}

function PlatformSettingsPanel({ api }) {
  const [contactNumber, setContactNumber] = useState('');
  const [defaultRate, setDefaultRate] = useState('500');
  const [alertEmail, setAlertEmail] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get('/api/sports/admin/platform-settings')
      .then(r => {
        setContactNumber(r.data.contact_number || '');
        setDefaultRate(String(r.data.default_per_court_rate ?? 500));
        setAlertEmail(r.data.billing_alert_email || '');
        setLoaded(true);
      })
      .catch(() => { setContactNumber('0995 256 8450'); setLoaded(true); });
  }, [api]);

  const saveAlertEmail = async () => {
    setSaving('alert');
    try {
      await api.put('/api/sports/admin/platform-settings', { billing_alert_email: alertEmail.trim() });
      toast.success(alertEmail.trim() ? 'Alert email saved' : 'Overdue alerts turned off');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(null); }
  };

  const saveContact = async () => {
    if (!contactNumber.trim()) { toast.error('Contact number cannot be empty'); return; }
    setSaving('contact');
    try {
      await api.put('/api/sports/admin/platform-settings', { contact_number: contactNumber.trim() });
      toast.success('Contact number updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(null); }
  };

  const saveRate = async () => {
    const n = parseFloat(defaultRate);
    if (Number.isNaN(n) || n < 0) { toast.error('Rate must be a number ≥ 0'); return; }
    setSaving('rate');
    try {
      const r = await api.put('/api/sports/admin/platform-settings', { default_per_court_rate: n });
      setDefaultRate(String(r.data.default_per_court_rate));
      toast.success(`Default per-court rate saved (₱${n.toLocaleString()})`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(null); }
  };

  if (!loaded) return null;
  const inp = { background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '8px 12px', outline: 'none', fontSize: 13, flex: 1, minWidth: 0 };
  const btn = (busy) => ({ background: '#E63946', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 12, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap' });

  return (
    <div data-testid="platform-settings-panel" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: '18px 22px', marginBottom: 12 }}>
      <div className="flex items-center gap-2 mb-1">
        <Phone className="h-4 w-4" style={{ color: '#E63946' }} />
        <p className="font-bold uppercase text-xs tracking-wider" style={{ color: '#E63946', fontFamily: 'Oswald, sans-serif' }}>Platform Contact Number</p>
      </div>
      <p className="text-xs mb-3" style={{ color: '#555' }}>
        Shown to tenants when their trial expires or is about to expire. Used on warning banners and booking pages.
      </p>
      <div className="flex items-center gap-2 mb-5">
        <input
          data-testid="platform-contact-input"
          value={contactNumber}
          onChange={e => setContactNumber(e.target.value)}
          placeholder="0995 256 8450"
          style={inp}
        />
        <button
          data-testid="platform-contact-save"
          onClick={saveContact}
          disabled={saving === 'contact'}
          style={btn(saving === 'contact')}>
          {saving === 'contact' ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div style={{ borderTop: '1px solid #1E1E1E', paddingTop: 14 }}>
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="h-4 w-4" style={{ color: '#FFB300' }} />
          <p className="font-bold uppercase text-xs tracking-wider" style={{ color: '#FFB300', fontFamily: 'Oswald, sans-serif' }}>Default Price per Court (₱ / month)</p>
        </div>
        <p className="text-xs mb-3" style={{ color: '#555' }}>
          Base rate applied to per-court billing when a tenant has no custom rate set. Individual tenants can still override this via the per-org &quot;₱ / court / mo&quot; field below.
        </p>
        <div className="flex items-center gap-2">
          <input
            data-testid="platform-default-rate-input"
            type="number"
            min="0"
            step="10"
            value={defaultRate}
            onChange={e => setDefaultRate(e.target.value)}
            placeholder="500"
            style={{ ...inp, maxWidth: 160 }}
          />
          <span style={{ color: '#555', fontSize: 12 }}>PHP / court / month</span>
          <div style={{ flex: 1 }} />
          <button
            data-testid="platform-default-rate-save"
            onClick={saveRate}
            disabled={saving === 'rate'}
            style={btn(saving === 'rate')}>
            {saving === 'rate' ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div style={{ borderTop: '1px solid #1E1E1E', paddingTop: 14, marginTop: 14 }}>
        <div className="flex items-center gap-2 mb-1">
          <Mail className="h-4 w-4" style={{ color: '#FF9800' }} />
          <p className="font-bold uppercase text-xs tracking-wider" style={{ color: '#FF9800', fontFamily: 'Oswald, sans-serif' }}>Billing Alert Email</p>
        </div>
        <p className="text-xs mb-3" style={{ color: '#555' }}>
          Gets an email whenever a tenant goes past its renewal date. Leave empty to turn these alerts off.
        </p>
        <div className="flex items-center gap-2">
          <input
            data-testid="platform-alert-email-input"
            type="email"
            value={alertEmail}
            onChange={e => setAlertEmail(e.target.value)}
            placeholder="you@agri-books.com"
            style={inp}
          />
          <button
            data-testid="platform-alert-email-save"
            onClick={saveAlertEmail}
            disabled={saving === 'alert'}
            style={btn(saving === 'alert')}>
            {saving === 'alert' ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function KeyRow({ label, hint, prefix, value, onChange, show, setShow, testId, currentMasked, source }) {
  const inp = { background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '8px 12px', outline: 'none', fontSize: 13, flex: 1, minWidth: 0, fontFamily: 'JetBrains Mono, monospace' };
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#A3A3A3' }}>{label}</p>
        <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: source === 'db' ? '#00E67614' : source === 'env' ? '#FFB30014' : '#333', color: source === 'db' ? '#00E676' : source === 'env' ? '#FFB300' : '#777' }}>
          {source === 'db' ? 'DB (rotatable)' : source === 'env' ? 'ENV (.env)' : 'NOT SET'}
        </span>
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#777' }}>
          {currentMasked || '—'}
        </span>
      </div>
      <p className="text-xs mb-1.5" style={{ color: '#555' }}>{hint}</p>
      <div className="flex items-center gap-2">
        <input
          data-testid={testId}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={`New ${label.toLowerCase()} (${prefix}…)`}
          autoComplete="off"
          style={inp}
        />
        <button
          type="button"
          onClick={() => setShow(v => !v)}
          data-testid={`${testId}-toggle`}
          style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#777', borderRadius: 8, padding: '8px 10px', cursor: 'pointer' }}
          aria-label={show ? 'Hide' : 'Show'}>
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function PayMongoKeysPanel({ api }) {
  const [cfg, setCfg] = useState(null);
  const [secretKey, setSecretKey] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [showS, setShowS] = useState(false);
  const [showP, setShowP] = useState(false);
  const [showW, setShowW] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const fetchCfg = useCallback(() => {
    api.get('/api/sports/admin/paymongo-config')
      .then(r => setCfg(r.data))
      .catch(() => toast.error('Failed to load PayMongo config'));
  }, [api]);

  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const saveKeys = async () => {
    const body = {};
    if (secretKey.trim()) body.secret_key = secretKey.trim();
    if (publicKey.trim()) body.public_key = publicKey.trim();
    if (webhookSecret.trim()) body.webhook_secret = webhookSecret.trim();
    if (Object.keys(body).length === 0) { toast.error('Enter at least one key to rotate'); return; }
    setSaving(true);
    try {
      const r = await api.put('/api/sports/admin/paymongo-config', body);
      setCfg(r.data);
      setSecretKey(''); setPublicKey(''); setWebhookSecret('');
      setShowS(false); setShowP(false); setShowW(false);
      toast.success('Keys rotated — cache refreshed, no restart needed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const r = await api.post('/api/sports/admin/paymongo-config/test');
      toast.success(`✓ ${r.data.mode?.toUpperCase() || 'OK'} — key accepted by PayMongo (${(r.data.payment_intent_id || '').slice(0, 12)}…)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  };

  if (!cfg) return null;

  const mode = cfg.mode || 'unknown';
  const modeColor = mode === 'live' ? '#FF2D55' : mode === 'test' ? '#00BCD4' : '#555';
  const modeLabel = mode === 'live' ? 'LIVE' : mode === 'test' ? 'TEST' : 'UNKNOWN';

  return (
    <div data-testid="paymongo-keys-panel" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: '18px 22px', marginBottom: 12 }}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <KeyRound className="h-4 w-4" style={{ color: '#E63946' }} />
        <p className="font-bold uppercase text-xs tracking-wider" style={{ color: '#E63946', fontFamily: 'Oswald, sans-serif' }}>PayMongo Platform Keys</p>
        <span data-testid="paymongo-mode-pill" style={{ fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 5, background: `${modeColor}20`, color: modeColor, letterSpacing: 1 }}>
          {modeLabel}
        </span>
        <div style={{ flex: 1 }} />
        <button
          data-testid="paymongo-test-btn"
          onClick={runTest}
          disabled={testing}
          style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#00BCD4', borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: testing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: testing ? 0.6 : 1 }}>
          <Wifi className="h-3.5 w-3.5" /> {testing ? 'Testing…' : 'Test Connection'}
        </button>
      </div>
      <p className="text-xs mb-4" style={{ color: '#555', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Lock className="h-3 w-3" /> Saved keys live in the database and take priority over .env. Rotation is instant — no server restart needed. Leave a field empty to keep the current value.
      </p>

      <KeyRow label="Secret Key" prefix="sk_test_ or sk_live_"
        hint="Server-side key used for /payment_intents and refunds. Never expose to browsers."
        value={secretKey} onChange={setSecretKey} show={showS} setShow={setShowS}
        testId="paymongo-secret-input"
        currentMasked={cfg.secret_key_masked} source={cfg.secret_source} />

      <KeyRow label="Public Key" prefix="pk_test_ or pk_live_"
        hint="Browser-safe key used by PayMongo hosted checkout."
        value={publicKey} onChange={setPublicKey} show={showP} setShow={setShowP}
        testId="paymongo-public-input"
        currentMasked={cfg.public_key_masked} source={cfg.public_source} />

      <KeyRow label="Webhook Signing Secret" prefix="whsk_"
        hint="Used to verify PayMongo webhook signatures. Get this from Developer Tools → Webhooks."
        value={webhookSecret} onChange={setWebhookSecret} show={showW} setShow={setShowW}
        testId="paymongo-webhook-input"
        currentMasked={cfg.webhook_secret_masked} source={cfg.webhook_source} />

      <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 6 }}>
        <button
          data-testid="paymongo-save-btn"
          onClick={saveKeys}
          disabled={saving}
          style={{ background: '#E63946', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 22px', fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Rotating…' : 'Rotate Keys'}
        </button>
        {cfg.updated_at && (
          <span style={{ fontSize: 10, color: '#555' }}>
            Last rotated: {new Date(cfg.updated_at).toLocaleString()} {cfg.updated_by ? `by ${cfg.updated_by}` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

function EmailSettingsPanel({ api }) {  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(null);

  const fetchSettings = useCallback(() => {
    api.get('/api/sports/admin/email-settings')
      .then(r => setSettings(r.data))
      .catch(() => toast.error('Failed to load email settings'));
  }, [api]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggle = async (field) => {
    if (!settings) return;
    const newVal = !settings[field];
    setSaving(field);
    try {
      const r = await api.put('/api/sports/admin/email-settings', { [field]: newVal });
      setSettings({ booking_reminders_enabled: r.data.booking_reminders_enabled, tournament_emails_enabled: r.data.tournament_emails_enabled });
      toast.success(`${field === 'booking_reminders_enabled' ? 'Booking reminders' : 'Tournament emails'} ${newVal ? 'enabled' : 'disabled'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(null); }
  };

  if (!settings) return null;

  const ToggleRow = ({ field, icon: Icon, label, desc, enabled }) => (
    <div data-testid={`email-toggle-row-${field}`} className="flex items-center justify-between gap-4 py-3" style={{ borderBottom: '1px solid #1A1A1A' }}>
      <div className="flex items-center gap-3 min-w-0">
        <div style={{ background: enabled ? '#E6394614' : '#1A1A1A', borderRadius: 8, padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon className="h-4 w-4" style={{ color: enabled ? '#E63946' : '#444' }} />
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: '#ccc' }}>{label}</p>
          <p className="text-xs" style={{ color: '#555' }}>{desc}</p>
        </div>
      </div>
      <button
        data-testid={`email-toggle-btn-${field}`}
        onClick={() => toggle(field)}
        disabled={saving === field}
        style={{
          width: 48, height: 26, borderRadius: 13, border: 'none', cursor: saving === field ? 'not-allowed' : 'pointer',
          background: enabled ? '#E63946' : '#2A2A2A', position: 'relative', transition: 'background 0.2s',
          flexShrink: 0,
        }}
      >
        <span style={{
          position: 'absolute', top: 3, left: enabled ? 25 : 3,
          width: 20, height: 20, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }} />
      </button>
    </div>
  );

  return (
    <div data-testid="email-settings-panel" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: '18px 22px', marginBottom: 0 }}>
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-4 w-4" style={{ color: '#E63946' }} />
        <p className="font-bold uppercase text-xs tracking-wider" style={{ color: '#E63946', fontFamily: 'Oswald, sans-serif' }}>Resend Email Controls</p>
      </div>
      <p className="text-xs mb-4" style={{ color: '#555' }}>Toggle automated reminders. Transactional emails (booking confirmed, declined, proof received) are always active.</p>
      <ToggleRow
        field="booking_reminders_enabled"
        icon={Bell}
        label="Booking Reminders"
        desc="24-hour and 1-hour pre-booking reminder emails"
        enabled={settings.booking_reminders_enabled}
      />
      <ToggleRow
        field="tournament_emails_enabled"
        icon={Trophy}
        label="Tournament Notifications"
        desc="Bracket-day match alerts to registered players"
        enabled={settings.tournament_emails_enabled}
      />
      <ToggleRow
        field="billing_reminders_enabled"
        icon={CreditCard}
        label="Renewal Reminders"
        desc="Subscription emails to facility owners: 7 days before, 3 days before, and when overdue"
        enabled={settings.billing_reminders_enabled}
      />
    </div>
  );
}

// Platform feature flags — bracket-managed tournaments are hidden while
// debugged internally (owner 2026-08-17); this is the click-to-reactivate.
function FeatureFlagsPanel({ api }) {
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/api/sports/admin/feature-flags')
      .then(r => setFlags(r.data))
      .catch(() => toast.error('Failed to load feature flags'));
  }, [api]);
  if (!flags) return null;
  const enabled = !!flags.full_tournaments_enabled;
  const toggle = async () => {
    setSaving(true);
    try {
      const r = await api.put('/api/sports/admin/feature-flags', { full_tournaments_enabled: !enabled });
      setFlags(r.data);
      toast.success(`Bracket-managed tournaments ${!enabled ? 'ENABLED for all facilities' : 'hidden (announce-style only)'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="feature-flags-panel" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: '18px 22px', marginTop: 14 }}>
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="h-4 w-4" style={{ color: '#E63946' }} />
        <p className="font-bold uppercase text-xs tracking-wider" style={{ color: '#E63946', fontFamily: 'Oswald, sans-serif' }}>Feature Flags</p>
      </div>
      <p className="text-xs mb-4" style={{ color: '#555' }}>Platform-wide switches — apply to every facility.</p>
      <div data-testid="flag-row-full-tournaments" className="flex items-center justify-between gap-4 py-3">
        <div>
          <p className="text-sm font-semibold" style={{ color: '#ccc' }}>Bracket-Managed Tournaments</p>
          <p className="text-xs" style={{ color: '#555' }}>OFF = facilities can only create announcement-style tournaments (bracket system hidden while it is debugged). Existing bracket events keep working either way.</p>
        </div>
        <button data-testid="flag-toggle-full-tournaments" onClick={toggle} disabled={saving}
          style={{ width: 48, height: 26, borderRadius: 13, border: 'none', cursor: saving ? 'not-allowed' : 'pointer', background: enabled ? '#E63946' : '#2A2A2A', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
          <span style={{ position: 'absolute', top: 3, left: enabled ? 25 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
        </button>
      </div>
    </div>
  );
}

// ── Area shell: /sports/super-admin/* ─────────────────────────────────────────
// Stable header + area nav (Tenants · Platform Settings); route content below.
function SuperAdminShell({ title, active, headerRight = null, children }) {
  const navigate = useNavigate();
  const NAV = [
    { k: 'tenants', label: 'Tenants', to: '/sports/super-admin/tenants' },
    { k: 'platform', label: 'Platform Settings', to: '/sports/super-admin/platform' },
    { k: 'devices', label: 'Devices', to: '/sports/super-admin/devices' },   // K6 kiosk fleet
    { k: 'live', label: 'Live', to: '/sports/super-admin/live' },            // K8 P2.5 P4: machines in use now
    { k: 'sales', label: 'Sales', to: '/sports/super-admin/sales' },         // K6 P2
    { k: 'review', label: 'Review', to: '/sports/super-admin/review' },      // K6 P2
    { k: 'vouchers', label: 'Vouchers', to: '/sports/super-admin/vouchers' }, // K6 P4
  ];
  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <div style={{ background: '#0D0D0D', padding: '18px 16px 0 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
            <button onClick={() => navigate('/superadmin')} data-testid="sports-admin-back-btn"
              style={{ background: 'transparent', border: 'none', color: '#555', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px', flexShrink: 0 }}>
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#E63946', fontWeight: 700, marginBottom: 2 }}>Super Admin — Sports Mode</p>
              <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(17px, 5vw, 24px)', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1, overflowWrap: 'anywhere' }}>{title}</h1>
            </div>
          </div>
          {headerRight}
        </div>
        <div style={{ display: 'flex', gap: 4, marginTop: 14, borderBottom: '1px solid #1A1A1A', overflowX: 'auto' }}>
          {NAV.map(n => (
            <button key={n.k} onClick={() => navigate(n.to)} data-testid={`sports-admin-nav-${n.k}`}
              style={{ padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1,
                color: active === n.k ? '#E63946' : '#555',
                borderBottom: `2px solid ${active === n.k ? '#E63946' : 'transparent'}`, marginBottom: -1 }}>
              {n.label}
            </button>
          ))}
        </div>
      </div>
      {children}
    </div>
  );
}

// ── /sports/super-admin/platform — platform configuration, three facets ──────
export function SportsSuperAdminPlatformPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem('agripos_token');
  const api = useMemo(() => sportsAdminApi(token), [token]);
  const tab = searchParams.get('tab') || 'general';
  const TABS = [
    { k: 'general', label: 'General' },
    { k: 'payments', label: 'Payments' },
    { k: 'emails', label: 'Emails' },
  ];
  return (
    <SuperAdminShell title="Platform Settings" active="platform">
      <div style={{ padding: '20px 16px' }}>
        <div className="flex gap-2 mb-5 flex-wrap">
          {TABS.map(t => (
            <button key={t.k}
              onClick={() => setSearchParams(t.k === 'general' ? {} : { tab: t.k }, { replace: true })}
              data-testid={`sports-admin-platform-tab-${t.k}`}
              style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                background: tab === t.k ? '#E63946' : '#141414', color: tab === t.k ? '#fff' : '#555',
                border: `1px solid ${tab === t.k ? '#E63946' : '#2A2A2A'}` }}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ maxWidth: 780 }}>
          {tab === 'general' && <PlatformSettingsPanel api={api} />}
          {tab === 'payments' && <PayMongoKeysPanel api={api} />}
          {tab === 'emails' && <><EmailSettingsPanel api={api} /><FeatureFlagsPanel api={api} /></>}
        </div>
      </div>
    </SuperAdminShell>
  );
}

// ── /sports/super-admin/tenants — the tenant index ───────────────────────────
export default function SportsSuperAdminPage() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filter lives in the URL (?filter=grace_period) so a filtered view can be
  // refreshed, shared, and returned to with the back button (IA skill §6).
  const [filterParams, setFilterParams] = useSearchParams();
  const filter = filterParams.get('filter') || 'all';
  const setFilter = (k) => setFilterParams(k === 'all' ? {} : { filter: k }, { replace: true });
  const token = localStorage.getItem('agripos_token');
  const api = useMemo(() => sportsAdminApi(token), [token]);

  const fetchOrgs = () => {
    setLoading(true);
    api.get('/api/sports/admin/orgs')
      .then(r => setOrgs(r.data || []))
      .catch(e => {
        if (e.response?.status === 403) toast.error('Super admin access required');
        else toast.error('Failed to load sports orgs');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOrgs(); }, []);

  // Attention groups — tenants a platform admin should look at TODAY.
  const isExpiringSoon = o => o.account_status === 'active' && o.plan === 'tenant' && o.next_renewal_date
    && daysUntil(o.next_renewal_date) !== null && daysUntil(o.next_renewal_date) >= 0 && daysUntil(o.next_renewal_date) <= 7;
  const isMissingDate = o => ['active', 'grace_period'].includes(o.account_status) && o.plan === 'tenant' && !o.next_renewal_date;
  const attention = [
    { k: 'grace_period', label: 'In grace period', color: '#FF9800', list: orgs.filter(o => o.account_status === 'grace_period') },
    { k: 'expiring',     label: 'Expiring within 7 days', color: '#FFB300', list: orgs.filter(isExpiringSoon) },
    { k: 'no_date',      label: 'No expiry date', color: '#FF2D55', list: orgs.filter(isMissingDate) },
    { k: 'pending',      label: 'Awaiting approval', color: '#00BCD4', list: orgs.filter(o => o.account_status === 'pending') },
  ].filter(g => g.list.length > 0);

  const filtered =
    filter === 'all' ? orgs :
    filter === 'expiring' ? orgs.filter(isExpiringSoon) :
    filter === 'no_date' ? orgs.filter(isMissingDate) :
    orgs.filter(o => o.account_status === filter);
  const counts = ['all', 'pending', 'active', 'trial', 'grace_period', 'suspended', 'deactivated'].reduce((acc, k) => {
    acc[k] = k === 'all' ? orgs.length : orgs.filter(o => o.account_status === k).length;
    return acc;
  }, {});

  return (
    <SuperAdminShell title="Tenants" active="tenants"
      headerRight={
        <button onClick={fetchOrgs} style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373', borderRadius: 8, padding: '8px 16px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      }>
      {/* Attention strip — what needs a look today */}
      {!loading && attention.length > 0 && (
        <div data-testid="sports-admin-attention-strip"
          style={{ margin: '16px 28px 0 28px', background: '#141414', border: '1px solid #2A2A2A', borderRadius: 12, padding: '12px 16px' }}>
          <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#FF9800', fontWeight: 800 }}>Needs attention</p>
          <div className="flex gap-2 flex-wrap">
            {attention.map(g => (
              <button key={g.k} onClick={() => setFilter(filter === g.k ? 'all' : g.k)}
                data-testid={`sports-admin-attention-${g.k}`}
                style={{ padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  background: filter === g.k ? g.color : `${g.color}14`, color: filter === g.k ? '#000' : g.color,
                  border: `1px solid ${g.color}40` }}>
                {g.label} ({g.list.length})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ padding: '14px 28px', borderBottom: '1px solid #1A1A1A', display: 'flex', gap: 8, overflowX: 'auto' }}>
        {[
          { k: 'all',         label: 'All' },
          { k: 'pending',     label: 'Pending' },
          { k: 'active',      label: 'Active' },
          { k: 'trial',       label: 'Trial' },
          { k: 'grace_period',label: 'Grace' },
          { k: 'suspended',   label: 'Suspended' },
          { k: 'deactivated', label: 'Off' },
        ].map(({ k, label }) => (
          <button key={k} onClick={() => setFilter(k)}
            data-testid={`sports-admin-filter-${k}`}
            style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
              background: filter === k ? '#E63946' : '#141414',
              color: filter === k ? '#fff' : '#555',
              border: `1px solid ${filter === k ? '#E63946' : '#2A2A2A'}` }}>
            {label} {counts[k] > 0 ? `(${counts[k]})` : ''}
          </button>
        ))}
      </div>

      {/* Org list */}
      <div style={{ padding: '20px 28px' }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #E63946', borderTopColor: 'transparent' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" style={{ color: '#333' }}>
            <p className="text-4xl mb-3">🏟</p>
            <p className="text-sm">No orgs in this category.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(org => (
              <OrgCard key={org.id} org={org} api={api} onRefresh={fetchOrgs} />
            ))}
          </div>
        )}
      </div>
    </SuperAdminShell>
  );
}


// Shared with SportsSuperAdminTenantDetailPage.jsx (same area, split files).
export { sportsAdminApi, daysUntil, RenewalLine, StatusBadge, ApproveModal, SuperAdminShell, STATUS_CONFIG, PLAN_CONFIG };